import { useEffect, useRef, useCallback } from 'react';
import { NPC_PLAYER_ID, GamePhase } from '../gameLogic/constants.js';
import NPCActions from './NPCActions.js';

const NPCController = ({ gameState, onPlayCard, onEndTurn, onProvideInput, onPerformMulligan, onRedevelopmentPlan }) => {
    // Ref to hold the latest gameState
    const gameStateRef = useRef(gameState);

    // Update the ref whenever gameState changes
    useEffect(() => {
        gameStateRef.current = gameState;
    }, [gameState]);

    const handleNPCInput = useCallback((awaitingInput) => {
        let choice = null;
        const currentGameState = gameStateRef.current;
        
        switch (awaitingInput.type) {
            case 'CHOICE_CARD_TO_ADD':
                choice = NPCActions.makeStrategicChoice(awaitingInput.options, 'card_to_add', awaitingInput, currentGameState);
                break;
                
            case 'CHOICE_CARD_FROM_PILE':
                choice = NPCActions.makeStrategicChoice(awaitingInput.options, 'card_from_pile', awaitingInput, currentGameState);
                break;
                
            case 'CHOICE_CARD_FOR_EFFECT':
                choice = NPCActions.makeStrategicChoice(awaitingInput.options, 'card_for_effect', awaitingInput, currentGameState);
                break;
                
            case 'CHOICE_NUMBER':
                choice = NPCActions.makeRandomNumber(awaitingInput.min, awaitingInput.max);
                break;
                
            case 'CHOICE_CARDS_FOR_OPERATION':
                choice = NPCActions.makeRandomCardSelection(awaitingInput.options, awaitingInput.count);
                break;
                
            default:
                console.warn('[NPCController] Unknown input type:', awaitingInput.type);
                choice = awaitingInput.options?.[0] || null;
                break;
        }
        
        if (choice !== null) {
            onProvideInput(choice);
        }
    }, [onProvideInput]);

    const handleNPCTurn = useCallback((npcPlayer) => {
        const currentGameState = gameStateRef.current;
        
        // チュートリアル用のスクリプトがあるかチェック
        const npcScript = currentGameState.tutorial?.master?.npcScript;
        const currentRound = currentGameState.round_number;
        
        if (npcScript && npcScript[currentRound]) {
            const turnScript = npcScript[currentRound];
            console.log(`[NPC] Following Tutorial Script for Round ${currentRound}`);
            NPCActions.executeScriptedTurn(npcPlayer, currentGameState, turnScript, onPlayCard, onEndTurn);
            return; // スクリプトがある場合はここで確実に終了し、通常のAIには渡さない
        }

        // スクリプトがない場合は通常の行動
        console.log('[NPC] No Script found. Using Normal AI.');

        // 再開発計画の使用判定（カードプレイ前に確認）
        if (NPCActions.shouldUseRedevelopmentPlan(npcPlayer, currentGameState)) {
            const targetCard = NPCActions.selectRedevelopmentTarget(npcPlayer);
            if (targetCard) {
                console.log('[NPC] Using Redevelopment Plan on:', targetCard.instance_id);
                onRedevelopmentPlan(NPC_PLAYER_ID, targetCard.instance_id);
                return;
            }
        }

        const cardPlayed = NPCActions.executeCardPlay(npcPlayer, currentGameState, onPlayCard);
        
        if (!cardPlayed) {
            // プレイ可能なカードがない場合はターン終了
            NPCActions.executeEndTurn(onEndTurn);
        }
        // カードをプレイした場合は、次の行動判定は次のuseEffectサイクルで行われる
    }, [onPlayCard, onEndTurn, onRedevelopmentPlan]);    const handleNPCAction = useCallback(() => {
        if (!gameStateRef.current || gameStateRef.current.game_over) return;

        const { current_turn, awaiting_input, players } = gameStateRef.current;
        const npcPlayer = players[NPC_PLAYER_ID];
        
        // 選択待ち状態の処理
        if (awaiting_input && awaiting_input.player_id === NPC_PLAYER_ID) {
            handleNPCInput(awaiting_input);
            return;
        }
        
        // NPCのターン時の処理
        if (current_turn === NPC_PLAYER_ID) {
            handleNPCTurn(npcPlayer);
            return;
        }
    }, [handleNPCInput, handleNPCTurn]);
    // Effect for handling regular turn actions and inputs
    useEffect(() => {
        if (!gameState || gameState.game_over || gameState.phase === GamePhase.MULLIGAN) return;

        const { current_turn, awaiting_input } = gameState;
        
        // NPCのターンかどうかチェック
        const isNPCTurn = current_turn === NPC_PLAYER_ID;
        
        // NPCが選択待ち状態かどうかチェック
        const isNPCAwaitingInput = awaiting_input && awaiting_input.player_id === NPC_PLAYER_ID;
        
        if (isNPCTurn || isNPCAwaitingInput) {
            // 2秒後にNPCの行動を実行
            const timer = setTimeout(() => {
                handleNPCAction();
            }, 2000);
            
            return () => clearTimeout(timer);
        }
    }, [gameState, handleNPCAction]);

    // Effect for handling the mulligan phase
    useEffect(() => {
        // Condition to initiate mulligan action
        const shouldInitiateMulligan = 
            gameState?.phase === GamePhase.MULLIGAN && 
            gameState.mulligan_state[NPC_PLAYER_ID].status === 'undecided'; // Access .status

        if (shouldInitiateMulligan) {
            // Execute immediately if condition is met
            const npcPlayer = gameState.players[NPC_PLAYER_ID];
            
            // NPCActions.jsでカード選択ロジックを実装
            const cardsToMulligan = NPCActions.decideMulligan(npcPlayer, gameState);
            const selectedCardIds = cardsToMulligan.map(card => card.instance_id);

            onPerformMulligan(selectedCardIds);
        }
    }, [gameState, onPerformMulligan]);


    // このコンポーネントは表示要素を持たない
    return null;
};

export default NPCController;